import { useEffect, useRef, useState } from 'react';
import {
  createChart,
  ColorType,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
} from 'lightweight-charts';
import type { IChartApi, ISeriesApi, CandlestickData, Time } from 'lightweight-charts';
import type { CandleData, SupportResistance } from '../types/trade';
import { api } from '../services/api';

interface TradingChartProps {
  symbol?: string;
  interval?: string;
  height?: number;
  showVolume?: boolean;
  supportResistance?: SupportResistance;
}

export function TradingChart({
  symbol = 'fco_usdt',
  interval = '1h',
  height = 400,
  showVolume = true,
  supportResistance,
}: TradingChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#1a1a2e' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2b2b43' },
        horzLines: { color: '#2b2b43' },
      },
      width: chartContainerRef.current.clientWidth,
      height: height,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1,
      },
    });

    chartRef.current = chart;

    // Add candlestick series (v5 API)
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });
    candlestickSeriesRef.current = candlestickSeries;

    // Add volume series if enabled
    if (showVolume) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: '#26a69a',
        priceFormat: { type: 'volume' },
        priceScaleId: '',
      });
      volumeSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
      });
      volumeSeriesRef.current = volumeSeries;
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    // Load data
    loadData();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [height, showVolume]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const candles = await api.getCandles(symbol, interval, 500);

      if (candlestickSeriesRef.current) {
        const candleData: CandlestickData<Time>[] = candles.map((c: CandleData) => ({
          time: c.time as Time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        }));
        candlestickSeriesRef.current.setData(candleData);
      }

      if (volumeSeriesRef.current && showVolume) {
        const volumeData = candles.map((c: CandleData) => ({
          time: c.time as Time,
          value: c.volume || 0,
          color: c.close >= c.open ? '#26a69a80' : '#ef535080',
        }));
        volumeSeriesRef.current.setData(volumeData);
      }

      // Add support/resistance lines
      if (supportResistance && chartRef.current) {
        addSupportResistanceLines(supportResistance);
      }

      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chart data');
      setLoading(false);
    }
  };

  const addSupportResistanceLines = (sr: SupportResistance) => {
    if (!chartRef.current) return;

    // Add support lines (green)
    sr.supports.forEach((level, i) => {
      if (i < 3 && chartRef.current) {
        const series = chartRef.current.addSeries(LineSeries, {
          color: '#26a69a',
          lineWidth: 1,
          lineStyle: 2, // Dashed
          title: `S${i + 1}`,
        });
        // Create horizontal line data
        const lineData = [
          { time: (Date.now() / 1000 - 86400 * 30) as Time, value: level.price },
          { time: (Date.now() / 1000) as Time, value: level.price },
        ];
        series.setData(lineData);
      }
    });

    // Add resistance lines (red)
    sr.resistances.forEach((level, i) => {
      if (i < 3 && chartRef.current) {
        const series = chartRef.current.addSeries(LineSeries, {
          color: '#ef5350',
          lineWidth: 1,
          lineStyle: 2,
          title: `R${i + 1}`,
        });
        const lineData = [
          { time: (Date.now() / 1000 - 86400 * 30) as Time, value: level.price },
          { time: (Date.now() / 1000) as Time, value: level.price },
        ];
        series.setData(lineData);
      }
    });
  };

  return (
    <div className="trading-chart">
      <div className="chart-header">
        <span className="symbol">{symbol.toUpperCase()}</span>
        <span className="interval">{interval}</span>
        {loading && <span className="loading">Loading...</span>}
        {error && <span className="error">{error}</span>}
      </div>
      <div ref={chartContainerRef} className="chart-container" />
    </div>
  );
}
